import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";

const texts = {
  title: "Partnership",
  heroImage: "https://net-basis.net/Bilder/netbasis-partnership-dach-market-entry.webp",
  heroAlt: "Businessman offers a handshake for a trusted partnership and DACH market entry.",
  heroClaimLine1: "Strong through",
  heroClaimLine2: "partnership!",
  h1Text: "Your reliable link for international market success. We bridge cultural, linguistic, and logistical hurdles to establish foreign ICT manufacturers safely and efficiently in the DACH region.",
  introParagraphs: [
    "The very moment words like \"international\" or \"abroad\" appear in your business thoughts and activities, the ensuing processes usually become more complex. Innumerable factors must be considered: language barriers, confusing legal situations, or even significant time differences can occur and disrupt established planning. Last but not least, the difficulty of being personally on-site to see what is really happening represents a major hurdle for many manufacturers.",
    "Exactly at this point, NetBasis International is the right partner by your side. As a long-established and highly experienced consulting, import, and trading company, we function as your strong local link. We act as your qualified mediator on the ground and reliably safeguard you against critical or business-ending errors that can arise when entering the international market.",
    "Our international manufacturer partners benefit directly from our deep market knowledge and our solid presence throughout the entire German-speaking economic area. As your operational importer, we bring you into direct contact with all relevant local sales companies and trade organizations in just one single step. With NetBasis International and the high-caliber contacts associated with us, launching outstanding products and forward-looking ICT solutions becomes significantly easier and more efficient.",
    "At the same time, our local sales partners appreciate cooperating with a company based in Germany like NetBasis International. This gives them immediate and uncomplicated access to a broad portfolio of innovative products and solutions from various foreign manufacturers. We are proud to already be working successfully with a large number of important, non-competing manufacturers.",
  ],
  experienceHeading: "A Wealth of Experience",
  experienceParagraphs: [
    "Markets change — knowledge, contacts, and experience remain.",
    "For nearly two decades, NetBasis International has been successfully building up foreign manufacturers and tech brands in the DACH region, establishing them sustainably in the market, and supporting them over the long term. For us, this is never just about pure sales. It is about genuine trust and a deep understanding of both sides — the global brand that wants to gain a foothold in a new market, and the local customer looking for a dependable partner.",
    "Good and reliable relationships with our partners are not a nice-to-have; they are the solid foundation for sustainable success and satisfied customers.",
    "Manufacturers come and go, but our ability to read markets precisely and create the right connections remains. Below you will see an excerpt of important partnerships from the past nearly twenty years.",
  ],
  manufacturers: [
    {
      name: "LeadDoubler ApS",
      logo: "https://net-basis.net/Bilder/Lead-Doubler-logo.png",
      desc: "Danish manufacturer of lead generation software",
    },
    {
      name: "Mondago Ltd",
      logo: "https://net-basis.net/Bilder/Mondago-logo.png",
      desc: "English manufacturer of CTI solutions",
    },
    {
      name: "Pinewood",
      logo: "https://net-basis.net/Bilder/Pinewood-logo.jpg",
      desc: "Belgian manufacturer of multimedia solutions",
    },
    {
      name: "Retell Call Recording",
      logo: "https://net-basis.net/Bilder/Re-Tell-logo.png",
      desc: "English manufacturer of call recording solutions",
    },
    {
      name: "Rohde & Schwarz Topex",
      logo: "https://net-basis.net/Bilder/Rohde-Schwarz-logo.jpg",
      desc: "Romanian manufacturer of GSM gateways",
    },
    {
      name: "Samsung",
      logo: "https://net-basis.net/Bilder/Samsung-logo.png",
      desc: "Collaboration in the area of their CTI solution",
    },
    {
      name: "Voice gear",
      logo: "https://net-basis.net/Bilder/Voice-Gear-logo.jpg",
      desc: "International trade brand for audio solutions",
    },
    {
      name: "Zenion",
      logo: "https://net-basis.net/Bilder/Zenion-logo.jpg",
      desc: "International trade brand for fanless mini PCs",
    },
  ],
  claim: "NetBasis International - drive to excellence with excellent drivers !",
};

const renderWithBold = (text: string) => {
  const parts = text ? text.split(COMPANY_NAME) : [];
  return parts.map((part, i) => (
    <span key={i}>
      {part}
      {i < parts.length - 1 && (
        <strong className="text-primary-600">{COMPANY_NAME}</strong>
      )}
    </span>
  ));
};

export default function PartnershipEnPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Partnership | NetBasis International"
        description="Established B2B partnerships and a deep wealth of experience in the DACH region. We create the right market connections for sustainable success."
        ogUrl="https://net-basis.net/en/partnership"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={texts.heroImage}
                alt={texts.heroAlt}
                title={texts.title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex flex-col items-end justify-center px-6 md:px-10 pointer-events-none">
                <div className="flex flex-col items-end -translate-y-14 md:-translate-y-28">
                  <p
                    className="text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-right animate-[bdTextSlide_12s_ease-in-out_infinite]"
                    style={{
                      fontFamily: '"Open Sans", sans-serif',
                      paddingRight: '0.5rem',
                    }}
                  >
                    <span className="block">{texts.heroClaimLine1}</span>
                    <span className="block">{texts.heroClaimLine2}</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            {/* WIDGET 1 - Überschrift (jetzt h2) */}
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {texts.title}
            </h2>

            {/* WIDGET 2 - H1: fetter Fließtext */}
            <h1 className="text-foreground-950 font-semibold text-sm md:text-base leading-relaxed mb-4">
              {texts.h1Text}
            </h1>

            {/* WIDGET 3 - normaler Text */}
            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              {texts.introParagraphs.map(
                (text, idx) =>
                  text && (
                    <p key={idx}>{renderWithBold(text)}</p>
                  )
              )}
            </div>

            {/* WIDGET 4 - Überschrift (h2) */}
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {texts.experienceHeading}
            </h2>

            {/* WIDGET 5 - normaler Text */}
            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              {texts.experienceParagraphs.map(
                (text, idx) =>
                  text && (
                    <p key={`exp-${idx}`}>{renderWithBold(text)}</p>
                  )
              )}
            </div>

            {/* Partner-Logos - UNVERÄNDERT */}
            <div className="mt-8 md:mt-10 space-y-6 md:space-y-8">
              {texts.manufacturers.map((m, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-6 md:gap-10"
                >
                  <div className="overflow-hidden w-36 h-24 sm:w-40 sm:h-28 md:w-48 md:h-32 flex-shrink-0 flex items-center justify-center">
                    <img loading="lazy"
                      src={m.logo}
                      alt=""
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-sm sm:text-lg md:text-xl font-bold text-primary-600 mb-1">
                      {m.name}
                    </h3>
                    <p className="text-xs sm:text-sm md:text-base text-foreground-950 leading-relaxed">
                      {m.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {texts.claim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}